import { CheckCircle2, Loader2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils.js';

export type StepStatus = 'pending' | 'active' | 'done' | 'error';

export interface AIStep {
  key: string;
  label: string;
  status: StepStatus;
  detail?: string;
}

interface AIReasoningStepperProps {
  steps: AIStep[];
  className?: string;
}

export function AIReasoningStepper({ steps, className }: AIReasoningStepperProps) {
  return (
    <div className={cn('space-y-2', className)} role="status" aria-label="AI processing steps" aria-live="polite">
      {steps.map((step) => (
        <div key={step.key} className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0">
            {step.status === 'done' && (
              <CheckCircle2 className="h-4 w-4 text-[oklch(0.55_0.14_155)]" aria-hidden="true" />
            )}
            {step.status === 'active' && (
              <Loader2 className="h-4 w-4 text-[oklch(0.44_0.09_262)] animate-spin" aria-hidden="true" />
            )}
            {(step.status === 'error' || step.status === 'pending') && (
              <Circle className={cn('h-4 w-4', step.status === 'error' ? 'text-[oklch(0.55_0.16_25)]' : 'text-[oklch(0.75_0.01_250)]')} aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0">
            <p
              className={cn(
                'text-sm font-medium leading-tight',
                step.status === 'done' && 'text-foreground',
                step.status === 'active' && 'text-[oklch(0.28_0.05_255)]',
                step.status === 'pending' && 'text-muted-foreground',
                step.status === 'error' && 'text-[oklch(0.55_0.16_25)]'
              )}
            >
              {step.label}
            </p>
            {step.detail && step.status === 'active' && (
              <p className="text-xs text-muted-foreground mt-0.5">{step.detail}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface StepDef {
  key: string;
  label: string;
}

export function useAIStepperState(stepDefs: StepDef[]) {
  const initial: AIStep[] = stepDefs.map(s => ({ ...s, status: 'pending' as StepStatus }));

  function advanceTo(steps: AIStep[], activeKey: string): AIStep[] {
    const activeIdx = stepDefs.findIndex(d => d.key === activeKey);
    return steps.map((s, idx) => {
      if (idx < activeIdx) return { ...s, status: 'done' as StepStatus };
      if (s.key === activeKey) return { ...s, status: 'active' as StepStatus };
      return { ...s, status: 'pending' as StepStatus };
    });
  }

  function completeAll(steps: AIStep[]): AIStep[] {
    return steps.map(s => ({ ...s, status: 'done' as StepStatus }));
  }

  function errorAt(steps: AIStep[], key: string): AIStep[] {
    return steps.map(s => s.key === key ? { ...s, status: 'error' as StepStatus } : s);
  }

  return { initial, advanceTo, completeAll, errorAt };
}
